import { useEffect, useState } from "react";
import vyvIcon from "@/assets/vyv-icon.png";
import { useLanguage } from "@/i18n/LanguageProvider";

interface AppSplashProps {
  /** Whether the splash is visible right now. */
  visible: boolean;
  /** Callback fired after the fade-out animation completes (300ms). */
  onHidden?: () => void;
}

/**
 * Branded full-screen loader. VYV logo on the dark Mirror background with a
 * soft cyan glow pulse. Used to cover handoffs that would otherwise flash
 * white — most notably login → Home, where the React unmount/remount cycle
 * exposes the body for a few frames.
 *
 * Designed to render instantly:
 *   - No external network requests (logo is bundled).
 *   - No web fonts (uses inline styles).
 *   - Inline base64-free; the logo is a 73 KB PNG imported by Vite.
 *
 * Render contract:
 *   - Mount the component (don't conditionally unmount) so the fade-out
 *     gets a chance to play. Pass `visible={false}` to fade out.
 *   - `onHidden` fires once the opacity transition completes, so the
 *     caller can fully unmount when desired.
 *
 * NOT a replacement for the IVC SplashHero (src/features/splash). That is
 * the pre-auth intro video — different moment, different purpose.
 */
const AppSplash = ({ visible, onHidden }: AppSplashProps) => {
  const [mounted, setMounted] = useState(visible);
  const [imageFailed, setImageFailed] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setImageFailed(false);
      return;
    }
    const t = window.setTimeout(() => {
      setMounted(false);
      onHidden?.();
    }, 320);
    return () => window.clearTimeout(t);
  }, [visible, onHidden]);

  if (!mounted) return null;

  return (
    <div
      aria-hidden={imageFailed ? undefined : true}
      role={imageFailed ? "alert" : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483600, // above modals + toasts
        background: "#06111C",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: visible ? 1 : 0,
        transition: "opacity 300ms ease-out",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <style>{`
        @keyframes vyv-splash-glow {
          0%, 100% { opacity: 0.55; transform: scale(0.96); filter: drop-shadow(0 0 18px hsl(187 72% 50% / 0.45)); }
          50%      { opacity: 1;    transform: scale(1.02); filter: drop-shadow(0 0 32px hsl(187 72% 50% / 0.65)); }
        }
        @keyframes vyv-splash-orb {
          0%, 100% { transform: translate(-50%, -50%) scale(0.9); opacity: 0.20; }
          50%      { transform: translate(-50%, -50%) scale(1.08); opacity: 0.35; }
        }
      `}</style>

      {/* Ambient orb behind the logo */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 320,
          height: 320,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, hsl(187 72% 50% / 0.22) 0%, transparent 70%)",
          animation: "vyv-splash-orb 2.6s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      {imageFailed ? <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <p style={{ color: "#00e5ff", font: "700 42px system-ui, sans-serif", letterSpacing: "0.12em" }}>VYV</p>
        <button type="button" onClick={() => window.location.reload()}
          style={{ minHeight: 48, padding: "12px 20px", border: "1px solid #00e5ff", borderRadius: 12,
            background: "#00e5ff", color: "#06111C", font: "600 16px system-ui, sans-serif" }}>
          {t("common.retry")}
        </button>
      </div> : <img
        src={vyvIcon}
        onError={() => setImageFailed(true)}
        alt=""
        width={108}
        height={108}
        style={{
          width: 108,
          height: 108,
          objectFit: "contain",
          animation: "vyv-splash-glow 2.2s ease-in-out infinite",
          position: "relative",
          zIndex: 1,
        }}
      />}
    </div>
  );
};

export default AppSplash;
